import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "../../lib/brand";
import Wordmark from "./Wordmark";
import { Shell } from "./Primitives";

const LINKS = [
  ["/services", "Services"],
  ["/solutions", "Solutions"],
  ["/industries", "Industries"],
  ["/features", "Features"],
  ["/how-it-works", "How It Works"],
  ["/pricing", "Pricing"],
  ["/case-studies", "Case Studies"],
  ["/about", "About"],
];

export default function SiteLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-elv-base">
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
          scrolled ? "border-elv-line bg-[#05070A]/85 backdrop-blur-xl" : "border-transparent bg-transparent"
        }`}
      >
        <Shell className="flex h-16 items-center justify-between gap-4">
          <Wordmark />
          <nav className="hidden items-center gap-1 xl:flex">
            {LINKS.map(([to, label]) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-link-${to.slice(1)}`}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-[13px] transition-colors duration-300 ${
                    isActive ? "text-elv-green" : "text-elv-dim hover:text-elv-text"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden rounded-md px-3 py-2 text-[13px] text-elv-dim transition-colors duration-300 hover:text-elv-text sm:inline-flex"
            >
              Client Login
            </Link>
            <Link
              to="/book-demo"
              data-testid="nav-book-demo"
              className="rounded-md bg-elv-green px-4 py-2 text-[13px] font-semibold text-[#04120A] transition-colors duration-300 hover:bg-[#5cffa0]"
            >
              Book a Free AI Demo
            </Link>
            <button
              type="button"
              aria-label="Toggle navigation"
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="glass rounded-md p-2 text-elv-text xl:hidden"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </Shell>
        {open ? (
          <div data-testid="nav-mobile-panel" className="border-t border-elv-line bg-[#05070A]/97 xl:hidden">
            <Shell className="grid gap-1 py-4">
              {[...LINKS, ["/contact", "Contact"], ["/login", "Client Login"], ["/signup", "Create Account"]].map(
                ([to, label]) => (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`mobile-nav-${to.slice(1)}`}
                    className="rounded-md px-3 py-3 text-sm text-elv-dim hover:bg-white/5 hover:text-elv-text"
                  >
                    {label}
                  </Link>
                )
              )}
            </Shell>
          </div>
        ) : null}
      </header>

      <main>
        <Outlet />
      </main>

      <footer data-testid="site-footer" className="relative border-t border-elv-line bg-[#04060A]">
        <Shell className="grid gap-12 py-16 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <Wordmark compact />
            <p className="mt-5 max-w-xs text-sm text-elv-dim">{BRAND.tagline}</p>
            <p className="mt-4 max-w-xs text-sm text-elv-dim">{BRAND.message}</p>
            <p data-testid="footer-owner-name" className="mt-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-elv-text">
              Rohit Bhattacharjee
            </p>
            <a
              href={`mailto:${BRAND.contactEmail}`}
              data-testid="footer-email"
              className="mt-2 inline-block font-mono text-sm text-elv-green hover:text-elv-cyan"
            >
              {BRAND.contactEmail}
            </a>
            <a
              href="tel:+917044436904"
              data-testid="footer-phone"
              className="mt-2 block font-mono text-sm text-elv-green hover:text-elv-cyan"
            >
              +91 70444 36904
            </a>
          </div>
          <FooterCol
            title="Platform"
            links={[
              ["/features", "Features"],
              ["/solutions", "Solutions"],
              ["/how-it-works", "How It Works"],
              ["/pricing", "Pricing"],
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              ["/about", "About"],
              ["/case-studies", "Case Studies"],
              ["/contact", "Contact"],
              ["/book-demo", "Book Demo"],
            ]}
          />
          <FooterCol
            title="Legal"
            links={[
              ["/privacy", "Privacy"],
              ["/terms", "Terms"],
              ["/login", "Client Login"],
              ["/signup", "Create Account"],
            ]}
          />
        </Shell>
        <Shell className="flex flex-col gap-3 border-t border-elv-line py-6 text-xs text-elv-dim sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} Elvoria AI. All rights reserved.</span>
          <span className="flex flex-wrap gap-4">
            {BRAND.social.map((s) => (
              <span key={s.label} data-testid={`footer-social-${s.label.toLowerCase()}`} title={s.note}>
                {s.label}
              </span>
            ))}
          </span>
        </Shell>
      </footer>
    </div>
  );
}

const FooterCol = ({ title, links }) => (
  <div>
    <h3 className="text-[11px] uppercase tracking-[0.24em] text-elv-text">{title}</h3>
    <ul className="mt-5 space-y-3">
      {links.map(([to, label]) => (
        <li key={to}>
          <Link
            to={to}
            data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="text-sm text-elv-dim transition-colors duration-300 hover:text-elv-green"
          >
            {label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);
